import { Link, useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import {
  LayoutDashboard,
  Briefcase,
  MessageSquare,
  FolderOpen,
  Users,
  UserCheck,
  FileText,
  Upload,
  Calendar,
  HelpCircle,
} from "lucide-react";
import logoLight from "@/assets/logo-light.png";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";

interface SellerSidebarProps {
  activeTab: string;
  onTabChange: (tab: string) => void;
}

const translations = {
  en: {
    dealPlatform: "Deal Platform",
    overview: "Overview",
    myDeals: "My Deals",
    messages: "Messages",
    documents: "Documents",
    calendar: "Calendar",
    consultants: "My Consultants",
    partners: "My Partners",
    experts: "Our Experts",
    submitDeal: "Submit Deal",
    external: "External",
    dealMonitor: "Deal Monitor",
    myConsultants: "My Consultants",
    assistance: "Need Assistance?",
  },
  fr: {
    dealPlatform: "Plateforme de transactions",
    overview: "Aperçu",
    myDeals: "Mes offres",
    messages: "Messages",
    documents: "Documents",
    calendar: "Calendrier",
    consultants: "Mes Consultants",
    partners: "Mes Partenaires",
    experts: "Nos Experts",
    submitDeal: "Soumettre une offre",
    external: "Externe",
    dealMonitor: "Deal Monitor",
    myConsultants: "Mes Consultants",
    assistance: "Besoin d'assistance ?",
  },
};

interface NavigationItem {
  name: string;
  tab: string;
  icon: React.ElementType;
  isExternal?: boolean;
  href?: string;
}

export const SellerSidebar = ({ activeTab, onTabChange }: SellerSidebarProps) => {
  const { user } = useAuth();
  const { language } = useLanguage();
  const t = translations[language as keyof typeof translations] || translations.en;

  const navigation: NavigationItem[] = [
    { name: t.overview, tab: "overview", icon: LayoutDashboard },
    { name: t.myDeals, tab: "my-deals", icon: Briefcase },
    { name: t.messages, tab: "messages", icon: MessageSquare },
    { name: t.documents, tab: "documents", icon: FolderOpen },
    { name: t.calendar, tab: "calendar", icon: Calendar },
    { name: t.partners, tab: "partners", icon: Users },
    { name: t.experts, tab: "experts", icon: FileText },
    { name: t.submitDeal, tab: "submit-deal", icon: Upload },
  ];

  const externalLinks = [
    { href: "/listings", name: t.dealMonitor, icon: LayoutDashboard },
    { href: "/seller/consultants", name: t.myConsultants, icon: UserCheck },
  ];

  return (
    <aside className="w-56 flex flex-col flex-shrink-0" style={{ background: "#1D314D" }}>
      <div className="px-5 pt-5 pb-4">
        <div className="h-px mb-4 rounded" style={{ background: "linear-gradient(90deg, #CEAB65 0%, rgba(206,171,101,0.1) 100%)" }} />
        <img src="/noBgColor.png" alt="AcquizGroup" className="h-7 w-auto" style={{ filter: "brightness(0) invert(1)" }} />
        <p className="text-[9px] tracking-widest uppercase mt-1.5" style={{ color: "rgba(255,255,255,0.3)" }}>{t.dealPlatform}</p>
        <div className="h-px mt-3" style={{ background: "linear-gradient(90deg, rgba(206,171,101,0.35) 0%, transparent 80%)" }} />
      </div>

      <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
        {navigation.map((item) => {
          const isActive = activeTab === item.tab;
          const Icon = item.icon;

          return (
            <button
              key={item.tab}
              onClick={() => onTabChange(item.tab)}
              className={cn(
                "flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors w-full text-left",
                isActive
                  ? "bg-[#CEAB65]/10 text-[#CEAB65] font-medium"
                  : "text-white/55 hover:bg-white/6 hover:text-white/85",
              )}
            >
              <Icon className="h-5 w-5" />
              {item.name}
            </button>
          );
        })}

        {/* External Links Section */}
        <div className="pt-4 mt-4" style={{ borderTop: "0.5px solid rgba(255,255,255,0.1)" }}>
          <p className="text-[8.5px] tracking-widest uppercase px-2 py-2" style={{ color: "rgba(255,255,255,0.25)" }}>
            {t.external}
          </p>
          {externalLinks.map((item) => {
            const Icon = item.icon;
            return (
              <Link
                key={item.href}
                to={item.href}
                className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors text-left text-white/55 hover:bg-white/6 hover:text-white/85"
              >
                <Icon className="h-5 w-5" />
                {item.name}
              </Link>
            );
          })}
        </div>

        {/* Assistance Button */}
        <div className="pt-4 mt-4 border-t border-border">
          <Link
            to="/contact"
            className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors text-left bg-[#CEAB65]/10 text-[#CEAB65] hover:bg-[#CEAB65]/20"
          >
            <HelpCircle className="h-5 w-5" />
            {t.assistance}
          </Link>
        </div>
      </nav>

      {user && (
        <div className="p-4 border-t border-border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <Users className="h-5 w-5 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{user.email}</p>
              <p className="text-xs text-muted-foreground capitalize">Seller</p>
            </div>
          </div>
        </div>
      )}
    </aside>
  );
};
